import { createContext, useContext, useEffect, useMemo, useState } from "react";
import siteEn from "./content/site.json";
import pricingEn from "./content/pricing.json";
import videoEn from "./content/video.json";
import instagramEn from "./content/instagram.json";
import zurichScheduleEn from "./content/schedule-zurich.json";
import silverScheduleEn from "./content/schedule-silver.json";
import uiEn from "./content/ui-en.json";
import de from "./content/de.json";
import faq from "./content/faq.json";
import { SEO_PAGE_META } from "./seo-page-all-data";

const I18nContext = createContext(null);

const HOME_HERO_INTRO = {
  de: "Brazilian Jiu-Jitsu für Erwachsene, Kinder und Frauen – und für die ganze Familie. Mit Ambition, Sorgfalt und brasilianischer Energie in Zürich und Thalwil.",
  en: "Brazilian Jiu-Jitsu for adults, kids and women – and for the whole family. Taught with ambition, care and Brazilian energy in Zurich and Thalwil.",
};

const SCHEDULE_AUDIENCE_LABELS = {
  de: {
    all: "Alle Klassen",
    adults: "BJJ Erwachsene",
    women: "BJJ Frauen",
    kids: "BJJ Kinder",
    mma: "MMA",
  },
  en: {
    all: "All classes",
    adults: "Adult BJJ",
    women: "Women’s BJJ",
    kids: "Kids BJJ",
    mma: "MMA",
  },
};

const FAMILY_META = {
  "kids-bjj-zurich": {
    de: {
      title: "Kids BJJ Zürich | Brazilian Jiu-Jitsu für Kinder | Tiago de Paula",
      description: "Kids BJJ in Zürich bis 13 Jahre: Gi & No-Gi, altersgerechte Gruppen und optional Competition. BJJ für die ganze Familie – Programme für Kids & Eltern am selben Standort.",
    },
    en: {
      title: "Kids BJJ Zurich | Brazilian Jiu-Jitsu for Children | Tiago de Paula",
      description: "Kids BJJ in Zurich up to age 13: Gi & No-Gi, age-specific groups and optional competition. BJJ for the whole family – programmes for kids & parents at one location.",
    },
  },
  "kids-bjj-thalwil": {
    de: {
      title: "Kids BJJ Thalwil | Brazilian Jiu-Jitsu für Kinder | Tiago de Paula",
      description: "Kids BJJ in Thalwil ca. 3–12 Jahre: Gi & No-Gi, Girls Only und optional Competition. BJJ für die ganze Familie – Programme für Kids & Eltern am selben Standort.",
    },
    en: {
      title: "Kids BJJ Thalwil | Brazilian Jiu-Jitsu for Children | Tiago de Paula",
      description: "Kids BJJ in Thalwil ages 3–12: Gi & No-Gi, Girls Only and optional competition. BJJ for the whole family – programmes for kids & parents at one location.",
    },
  },
};

const LANDING_META = {
  "daypass": {
    de: { title: "Day Pass · CHF 35 · Ab 18 | Tiago de Paula BJJ", description: "Ein Trainingstag in Zürich oder Thalwil für CHF 35. Ausschliesslich ab 18 Jahren, mit Teilnahmevereinbarung und Onlinezahlung." },
    en: { title: "Book a Day Pass · CHF 35 · Adults 18+ | Tiago de Paula BJJ", description: "Train for the day in Zurich or Thalwil for CHF 35. Adults 18+ only, with a participation agreement and online payment." },
  },
  "bjj-zurich": {
    de: {
      title: "BJJ Zürich | Brazilian Jiu-Jitsu in Zürich | Tiago de Paula",
      description: "Brazilian Jiu-Jitsu in Zürich seit 2017. Gi & No-Gi für Anfänger, Fortgeschrittene und Wettkämpfer an der Forchstrasse 225. Probetraining buchen.",
    },
    en: {
      title: "BJJ Zurich | Brazilian Jiu-Jitsu in Zurich | Tiago de Paula",
      description: "Brazilian Jiu-Jitsu in Zurich since 2017. Gi & No-Gi for beginners, experienced practitioners and competitors at Forchstrasse 225. Book a trial class.",
    },
  },
  "bjj-after-40-50": {
    de: {
      title: "BJJ ab 40 & 50 | Zu alt für Brazilian Jiu-Jitsu? | Tiago de Paula",
      description: "BJJ ab 40 oder 50 in Zürich: sinnvoll einsteigen, Trainingsintensität steuern, Recovery beachten und ohne Wettkampfdruck lernen. Probetraining bei Tiago de Paula BJJ.",
    },
    en: {
      title: "BJJ After 40 & 50 | Too Old for Brazilian Jiu-Jitsu? | Tiago de Paula",
      description: "Starting BJJ after 40 or 50 in Zurich: manage training load, recovery and sparring sensibly while building technique without competition pressure. Book a trial class.",
    },
  },
  "bjj-rules": {
    de: {
      title: "BJJ Regeln | AJP & ADCC Rules + Referee Training | Tiago de Paula",
      description: "BJJ Regeln verständlich erklärt: AJP, ADCC, Scoring, legale Techniken und Referee-Ausbildung bei Tiago de Paula BJJ in Zürich und Thalwil.",
    },
    en: {
      title: "BJJ Rules | AJP & ADCC + Referee Training | Tiago de Paula",
      description: "Understand BJJ rules: AJP, ADCC, scoring, legal techniques and referee development at Tiago de Paula BJJ in Zurich and Thalwil.",
    },
  },
  "bjj-familie": {
    de: {
      title: "BJJ als Familie | Eltern & Kinder trainieren in Zürich & Thalwil",
      description: "Kann man als Familie BJJ trainieren? Ja. Altersgerechte Programme für Kinder und Erwachsene in Zürich und Thalwil – BJJ für die ganze Familie.",
    },
    en: {
      title: "Family BJJ | Parents & Kids Train in Zurich & Thalwil",
      description: "Can a whole family train BJJ? Yes. Age-appropriate programmes for kids and adults in Zurich and Thalwil – Brazilian Jiu-Jitsu for the whole family.",
    },
  },
  ...SEO_PAGE_META,
  ...FAMILY_META,
};

function mergeContent(base, overlay) {
  if (overlay === undefined) return base;
  if (Array.isArray(base) && Array.isArray(overlay)) {
    return base.map((item, index) => mergeContent(item, overlay[index]));
  }
  if (base && overlay && typeof base === "object" && typeof overlay === "object") {
    return Object.fromEntries(
      Array.from(new Set([...Object.keys(base), ...Object.keys(overlay)])).map((key) => [
        key,
        mergeContent(base[key], overlay[key]),
      ]),
    );
  }
  return overlay;
}

function translateSchedule(schedule, language, dojo) {
  if (language === "en") return schedule;
  const translations = de.schedules;
  const translateClass = (item) => ({
    ...item,
    title: item.title_de || translations.titles[item.title] || item.title,
    format: item.format_de || item.format,
    level: item.level_de || translations.levels[item.level] || item.level,
  });
  return {
    ...schedule,
    notice: schedule.notice_de || (dojo === "zurich" ? translations.zurich_notice : translations.silver_notice),
    classes: schedule.classes.map(translateClass),
    future_schedule: schedule.future_schedule ? {
      ...schedule.future_schedule,
      notice: schedule.future_schedule.notice_de || schedule.future_schedule.notice,
      classes: schedule.future_schedule.classes.map(translateClass),
    } : undefined,
  };
}

function pathLanguage() {
  if (typeof window === "undefined") return "de";
  if (window.location.pathname.startsWith("/en")) return "en";
  if (window.location.pathname.startsWith("/de")) return "de";
  return window.localStorage.getItem("tiago-bjj-language") === "en" ? "en" : "de";
}

function pathSuffix() {
  if (typeof window === "undefined") return "";
  return window.location.pathname.replace(/^\/(de|en)\/?/, "").replace(/^\/+|\/+$/g, "");
}

export function I18nProvider({ children, initialLanguage }) {
  const [language, setLanguageState] = useState(() => initialLanguage || pathLanguage());

  const content = useMemo(() => {
    if (language === "en") {
      return {
        site: {
          ...siteEn,
          hero: { ...siteEn.hero, intro: HOME_HERO_INTRO.en },
        },
        pricing: pricingEn,
        video: videoEn,
        instagram: instagramEn,
        zurichSchedule: zurichScheduleEn,
        silverSchedule: silverScheduleEn,
      };
    }
    const localizedSite = mergeContent(siteEn, de.site);
    return {
      site: {
        ...localizedSite,
        hero: { ...localizedSite.hero, intro: HOME_HERO_INTRO.de },
      },
      pricing: mergeContent(pricingEn, de.pricing),
      video: mergeContent(videoEn, de.video),
      instagram: mergeContent(instagramEn, de.instagram),
      zurichSchedule: translateSchedule(zurichScheduleEn, "de", "zurich"),
      silverSchedule: translateSchedule(silverScheduleEn, "de", "silver"),
    };
  }, [language]);

  const baseUi = language === "de" ? de.ui : uiEn;
  const ui = {
    ...baseUi,
    schedule: {
      ...baseUi.schedule,
      ...SCHEDULE_AUDIENCE_LABELS[language],
    },
  };

  const setLanguage = (nextLanguage) => {
    if (!["de", "en"].includes(nextLanguage)) return;
    window.localStorage.setItem("tiago-bjj-language", nextLanguage);
    const suffix = pathSuffix();
    const nextPath = suffix ? `/${nextLanguage}/${suffix}/` : `/${nextLanguage}/`;
    const nextUrl = `${nextPath}${window.location.search}${window.location.hash}`;
    window.history.pushState({ language: nextLanguage }, "", nextUrl);
    setLanguageState(nextLanguage);
  };

  const localizeNews = (posts) => {
    if (language === "en") return posts;
    return posts.map((post) => ({
      ...post,
      ...(de.news[post.slug] || {}),
      ...(post.title_de ? {
        title: post.title_de,
        category: post.category_de || post.category,
        excerpt: post.excerpt_de || post.excerpt,
        body: post.body_de || post.body,
        cta_label: post.cta_label_de || post.cta_label,
        image_alt: post.image_alt_de || post.image_alt,
      } : {}),
    }));
  };

  useEffect(() => {
    document.documentElement.lang = language === "de" ? "de-CH" : "en";

    const suffix = pathSuffix();
    const landingMeta = LANDING_META[suffix]?.[language];
    const pageTitle = landingMeta?.title || (language === "de"
      ? "Tiago de Paula BJJ | Zürich & Silver Coast"
      : "Tiago de Paula BJJ | Zurich & Silver Coast");
    const description = landingMeta?.description || (language === "de"
      ? "Brazilian Jiu-Jitsu, MMA & Striking in Zürich & Thalwil seit 2017. 45+ Lektionen pro Woche. BJJ für die ganze Familie: Programme für Erwachsene & Kids."
      : "Brazilian Jiu-Jitsu, MMA & striking in Zurich & Thalwil since 2017. 45+ weekly classes. BJJ for the whole family: programmes for adults & kids.");

    document.title = pageTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const origin = window.location.origin;
    const pageSuffix = landingMeta ? `${suffix}/` : "";
    const ensureLink = (rel, hreflang, href) => {
      const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
      let link = document.head.querySelector(selector);
      if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        if (hreflang) link.hreflang = hreflang;
        document.head.appendChild(link);
      }
      link.href = href;
    };
    ensureLink("alternate", "de-CH", `${origin}/de/${pageSuffix}`);
    ensureLink("alternate", "en", `${origin}/en/${pageSuffix}`);
    ensureLink("alternate", "x-default", `${origin}/de/${pageSuffix}`);
    ensureLink("canonical", "", `${origin}/${language}/${pageSuffix}`);

    if (landingMeta) {
      document.querySelector("#tiago-structured-data")?.remove();
    } else {
      let structuredData = document.querySelector("#tiago-structured-data");
      if (!structuredData) {
        structuredData = document.createElement("script");
        structuredData.id = "tiago-structured-data";
        structuredData.type = "application/ld+json";
        document.head.appendChild(structuredData);
      }
      structuredData.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          ...content.site.dojos.map((dojo) => ({
            "@type": "SportsActivityLocation",
            name: dojo.name,
            description: dojo.description,
            address: dojo.address,
            url: dojo.id === "zurich" ? `${origin}/${language}/bjj-zurich/` : `${origin}/${language}/bjj-thalwil/`,
            image: `${origin}${dojo.image}`,
            telephone: content.site.phone,
            email: content.site.email,
            sameAs: [content.site.instagram_profile],
          })),
          {
            "@type": "FAQPage",
            mainEntity: faq.items.map((item) => ({
              "@type": "Question",
              name: language === "de" ? item.question_de : item.question_en,
              acceptedAnswer: {
                "@type": "Answer",
                text: language === "de" ? item.answer_de : item.answer_en,
              },
            })),
          },
        ],
      });
    }

    const onPopState = () => setLanguageState(pathLanguage());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [language, content.site]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, content, ui, localizeNews }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
